/** @jsx jsx */
import { jsx, css } from '@emotion/core';

import React, { useContext, useEffect, useState } from 'react';
import { Source, Layer } from 'react-map-gl';
import { FeatureCollection } from 'geojson';
import { PspsStatus, PSPSZoneAttributes } from '../../types';
import { useOutageMapOptions } from '../../providers/OutageMapOptionsProvider';
import { featureFlagsContext } from '../../../../providers/FeatureFlagsProvider';
import { useTranslation } from '../../../../hooks/useTranslation';
import { CONSTANTS } from '../../constants';
import { useOutageMapContext } from '../../providers/OutageMapProvider';

interface Props {
  hoveredPSPSFeatureId?: string | null;
}

export const PSPSOverlayLayer = React.memo(
  ({ hoveredPSPSFeatureId }: Props) => {
    const [geojson, setGeojson] = useState<FeatureCollection | null>(null);
    const { t } = useTranslation();
    const { outageToggles, setOutageToggles } = useOutageMapOptions();
    const { pspsGeoJsonData } = useOutageMapContext();

    const colorCodes = {
      [PspsStatus.Warning]: t('PSPS_ZONE_COLOR_WARNING'),
      [PspsStatus.Likely]: t('PSPS_ZONE_COLOR_LIKELY'),
      [PspsStatus.Imminent]: t('PSPS_ZONE_COLOR_IMMINENT'),
      [PspsStatus.InProgress]: t('PSPS_ZONE_COLOR_INPROGRESS'),
      [PspsStatus.Restoration]: t('PSPS_ZONE_COLOR_RESTORATION'),
      [PspsStatus.NoAlerts]: t('PSPS_ZONE_COLOR_NOALERTS'),
    };

    const { isOutagesPSPSZonesEnabledOnLoad, loading } = useContext(
      featureFlagsContext,
    );

    useEffect(() => {
      if (!pspsGeoJsonData) {
        return;
      }
      //Filter out Inactive zones
      const features = pspsGeoJsonData.features?.filter(
        f =>
          (f.properties as PSPSZoneAttributes).Status !== PspsStatus.NoAlerts,
      );
      setGeojson({
        type: 'FeatureCollection',
        features,
      });
    }, [pspsGeoJsonData]);

    useEffect(() => {
      //Display PSPS layer on page load based on the feature flag
      if (!loading && isOutagesPSPSZonesEnabledOnLoad) {
        setOutageToggles(prev => ({
          ...prev,
          psps: true,
        }));
      }
    }, [loading]);

    // Mapbox will render only when window is defined (Gatsby SSR safety)
    if (typeof window === 'undefined') {
      return null;
    }

    return (
      <React.Fragment>
        {geojson && outageToggles.psps && (
          <Source id="arcgis-polygons" type="geojson" data={geojson}>
            {/* Fill layer for polygons */}
            <Layer
              id={CONSTANTS.MAP_CONFIG.pspsLayerId}
              type="fill"
              paint={{
                'fill-color': [
                  'match',
                  ['get', 'Status'],
                  PspsStatus.InProgress,
                  colorCodes[PspsStatus.InProgress],
                  PspsStatus.Likely,
                  colorCodes[PspsStatus.Likely],
                  PspsStatus.NoAlerts,
                  colorCodes[PspsStatus.NoAlerts],
                  PspsStatus.Warning,
                  colorCodes[PspsStatus.Warning],
                  PspsStatus.Restoration,
                  colorCodes[PspsStatus.Restoration],
                  PspsStatus.Imminent,
                  colorCodes[PspsStatus.Imminent],
                  /* default */ '#9E9E9E', // Gray for unknown
                ],
                'fill-opacity': 0.5,
              }}
            />

            <Layer
              id="polygon-fill"
              type="line"
              paint={{
                'line-color': [
                  'match',
                  ['get', 'Status'],
                  PspsStatus.InProgress,
                  colorCodes[PspsStatus.InProgress],
                  PspsStatus.Likely,
                  colorCodes[PspsStatus.Likely],
                  PspsStatus.NoAlerts,
                  colorCodes[PspsStatus.NoAlerts],
                  PspsStatus.Warning,
                  colorCodes[PspsStatus.Warning],
                  PspsStatus.Restoration,
                  colorCodes[PspsStatus.Restoration],
                  PspsStatus.Imminent,
                  colorCodes[PspsStatus.Imminent],
                  /* default */ '#9E9E9E', // Gray for unknown
                ],
                'line-width': 2,
              }}
              filter={['==', ['id'], hoveredPSPSFeatureId || '']}
            />
          </Source>
        )}
      </React.Fragment>
    );
  },
);
